/**
 * Date utility functions for consistent date handling across the application
 */
/**
 * Format a Date object to ISO string without milliseconds
 */
export function formatIso(date) {
    return date.toISOString().replace(/\.\d{3}Z$/, "Z");
}
/**
 * Get current timestamp as ISO string
 */
export function now() {
    return formatIso(new Date());
}
/**
 * Parse ISO string to Date object
 */
export function parseIso(isoString) {
    return new Date(isoString);
}
/**
 * Check if a date is in the past
 */
export function isPast(date) {
    const targetDate = typeof date === "string" ? parseIso(date) : date;
    return targetDate < new Date();
}
/**
 * Check if a date is in the future
 */
export function isFuture(date) {
    const targetDate = typeof date === "string" ? parseIso(date) : date;
    return targetDate > new Date();
}
/**
 * Get the difference in days between two dates
 */
export function daysDifference(date1, date2) {
    const d1 = typeof date1 === "string" ? parseIso(date1) : date1;
    const d2 = typeof date2 === "string" ? parseIso(date2) : date2;
    const diffTime = Math.abs(d2.getTime() - d1.getTime());
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
}
/**
 * Get the difference in hours between two dates
 */
export function hoursDifference(date1, date2) {
    const d1 = typeof date1 === "string" ? parseIso(date1) : date1;
    const d2 = typeof date2 === "string" ? parseIso(date2) : date2;
    const diffTime = Math.abs(d2.getTime() - d1.getTime());
    return Math.ceil(diffTime / (1000 * 60 * 60));
}
/**
 * Add days to a date
 */
export function addDays(date, days) {
    const targetDate = typeof date === "string" ? parseIso(date) : new Date(date);
    targetDate.setDate(targetDate.getDate() + days);
    return targetDate;
}
/**
 * Add hours to a date
 */
export function addHours(date, hours) {
    const targetDate = typeof date === "string" ? parseIso(date) : new Date(date);
    targetDate.setHours(targetDate.getHours() + hours);
    return targetDate;
}
/**
 * Format date for display (e.g., "Dec 25, 2024")
 */
export function formatDisplay(date) {
    const targetDate = typeof date === "string" ? parseIso(date) : date;
    return targetDate.toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
    });
}
/**
 * Format date and time for display (e.g., "Dec 25, 2024 at 3:30 PM")
 */
export function formatDateTime(date) {
    const targetDate = typeof date === "string" ? parseIso(date) : date;
    return targetDate.toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "numeric",
        minute: "2-digit",
        hour12: true,
    });
}
/**
 * Format relative time (e.g., "2 hours ago", "in 3 days")
 */
export function formatRelative(date) {
    const targetDate = typeof date === "string" ? parseIso(date) : date;
    const now = new Date();
    const diffMs = targetDate.getTime() - now.getTime();
    const diffMinutes = Math.round(diffMs / (1000 * 60));
    const diffHours = Math.round(diffMs / (1000 * 60 * 60));
    const diffDays = Math.round(diffMs / (1000 * 60 * 60 * 24));
    if (Math.abs(diffMinutes) < 60) {
        if (diffMinutes === 0)
            return "now";
        return diffMinutes > 0 ? `in ${diffMinutes} minutes` : `${Math.abs(diffMinutes)} minutes ago`;
    }
    if (Math.abs(diffHours) < 24) {
        return diffHours > 0 ? `in ${diffHours} hours` : `${Math.abs(diffHours)} hours ago`;
    }
    return diffDays > 0 ? `in ${diffDays} days` : `${Math.abs(diffDays)} days ago`;
}
/**
 * Check if two dates are on the same day
 */
export function isSameDay(date1, date2) {
    const d1 = typeof date1 === "string" ? parseIso(date1) : date1;
    const d2 = typeof date2 === "string" ? parseIso(date2) : date2;
    return d1.getFullYear() === d2.getFullYear() && d1.getMonth() === d2.getMonth() && d1.getDate() === d2.getDate();
}
/**
 * Get start of day for a given date
 */
export function startOfDay(date) {
    const targetDate = typeof date === "string" ? parseIso(date) : new Date(date);
    targetDate.setHours(0, 0, 0, 0);
    return targetDate;
}
/**
 * Get end of day for a given date
 */
export function endOfDay(date) {
    const targetDate = typeof date === "string" ? parseIso(date) : new Date(date);
    targetDate.setHours(23, 59, 59, 999);
    return targetDate;
}
